import { useEffect, useRef, useCallback } from 'react';
import { auth } from '../firebaseConfig';
import { signInWithCustomToken } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { GAUtils } from '../model/GAUtils';

interface TelegramAuthData {
    id: number;
    first_name: string;
    last_name?: string;
    username?: string;
    photo_url?: string;
    auth_date: number;
    hash: string;
}

interface TelegramLoginButtonProps {
    onError: (message: string) => void;
    onSigningIn: (signing: boolean) => void;
    disabled?: boolean;
}

const TelegramLoginButton = ({ onError, onSigningIn, disabled }: TelegramLoginButtonProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const scriptLoadedRef = useRef(false);

    const handleTelegramAuth = useCallback(async (data: TelegramAuthData) => {
        try {
            onSigningIn(true);
            onError('');
            GAUtils.trackLoginAttempt('telegram');

            const functions = getFunctions();
            const telegramAuth = httpsCallable(functions, 'telegramAuth');
            const result = await telegramAuth(data);
            const { token } = result.data as { token: string };

            await signInWithCustomToken(auth, token);
            // Navigation handled by useAuthState in Login.tsx
        } catch (error: any) {
            console.error('Error signing in with Telegram:', error);
            GAUtils.trackLoginError('telegram', error.message || 'Unknown error', error.code);
            onError('Failed to sign in with Telegram. Please try again.');
        } finally {
            onSigningIn(false);
        }
    }, [onError, onSigningIn]);

    useEffect(() => {
        // Expose callback globally for the Telegram widget
        (window as any).onTelegramAuth = (data: TelegramAuthData) => {
            handleTelegramAuth(data);
        };

        if (scriptLoadedRef.current || !containerRef.current) return;

        const botName = process.env.REACT_APP_TELEGRAM_BOT_NAME || 'XTEASearchBot';

        const script = document.createElement('script');
        script.src = 'https://telegram.org/js/telegram-widget.js?22';
        script.setAttribute('data-telegram-login', botName);
        script.setAttribute('data-size', 'large');
        script.setAttribute('data-onauth', 'onTelegramAuth(user)');
        script.setAttribute('data-request-access', 'write');
        script.async = true;

        containerRef.current.appendChild(script);
        scriptLoadedRef.current = true;

        return () => {
            delete (window as any).onTelegramAuth;
        };
    }, [handleTelegramAuth]);

    return (
        <div
            ref={containerRef}
            className="telegram-login-wrapper"
            style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '8px',
                opacity: disabled ? 0.6 : 1,
                pointerEvents: disabled ? 'none' : 'auto',
            }}
        />
    );
};

export default TelegramLoginButton;
